import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  downloadPublicHolidays,
  getPublicHolidays,
  getPublicHolidaysByNextUrl,
  removePublicHoliday,
  updatePublicHoliday,
  uploadPublicHolidays,
} from '~hooks/usePublicHoliday/api';
import { PublicHolidays, PublicHolidaysResponse } from '~hooks/usePublicHoliday/domain';

const usePublicHolidays = () => {
  const [holidays, setHolidays] = useState<PublicHolidays[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const [timezoneFilter, setTimezoneFilter] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const [postcodeFilter, setPostcodeFilter] = useState<string>('');
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: PublicHolidaysResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getPublicHolidaysByNextUrl(nextUrl, { cancelToken: axiosCancelRef.current });
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if the request is cancelled
      if (resp === undefined) return;
      setHolidays((prev) => [...prev, ...resp!.publicHolidays]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const load = useCallback(async () => {
    axiosCancelRef.current?.cancel();
    setLoading(true);
    setError(false);

    try {
      const data = await getPublicHolidays({
        search: search || undefined,
        timezones: timezoneFilter.length > 0 ? timezoneFilter : undefined,
        postcode: postcodeFilter || undefined,
        year: yearFilter,
      });
      if (data.nextPageUrl !== null) {
        setNextUrl(data.nextPageUrl);
      }
      setHolidays(data.publicHolidays);
      setHasMore(data.nextPageUrl !== null);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [search, timezoneFilter, postcodeFilter, yearFilter]);

  const loadMore = async () => {
    if (!nextUrl) return;
    const data = await getPublicHolidaysByNextUrl(nextUrl);
    if (data) {
      setHolidays((prev) => [...prev, ...data.publicHolidays]);
      setNextUrl(data.nextPageUrl);
      setHasMore(data.nextPageUrl !== null);
    }
  };

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  const update = useCallback(
    async (oldDate: string, oldName: string, data: PublicHolidays) => {
      try {
        await updatePublicHoliday(oldDate, oldName, data);
      } catch (e) {
        return Promise.reject(e);
      }

      await load();
    },
    [load],
  );

  const remove = useCallback(
    async (data: PublicHolidays) => {
      try {
        await removePublicHoliday(data);
      } catch (e) {
        return Promise.reject(e);
      }

      await load();
    },
    [load],
  );

  const upload = useCallback(
    async (file: File, onUploadProgress: (progress: number) => void) => {
      try {
        const result = await uploadPublicHolidays(file, onUploadProgress);
        await load();
        return result;
      } catch (e) {
        return Promise.reject(e);
      }
    },
    [load],
  );

  const download = useCallback(async (year: number) => {
    try {
      await downloadPublicHolidays(year);
    } catch (e) {
      return Promise.reject(e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    holidays,
    update,
    remove,
    upload,
    download,
    loading,
    error,
    timezoneFilter,
    hasMore,
    loadMore,
    nextUrl,
    search,
    yearFilter,
    postcodeFilter,
    setSearch,
    setTimezoneFilter,
    reload: load,
    intersectionObserverRef,
    setPostcodeFilter,
    setYearFilter,
  };
};

export default usePublicHolidays;
